import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import {
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { Icon } from '../icon/Icon';
import styles from './Toggle.module.scss';

export type IconVisibility = 'only-on' | 'only-off' | 'both' | 'none';
export type HighlightMode = 'both' | 'only-on';

export interface ToggleProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onToggle'> {
  /** DOM id; pairs the surrounding `<label>` with the `<input>` via `htmlFor`. */
  id?: string;
  /** Initial / external checked state. The component mirrors this into local state on every change. Defaults to `false`. */
  isChecked?: boolean;
  /** Called with the new checked state and the underlying change event when the user toggles. */
  onToggle?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  /** Optional label rendered to the left of the switch (typically the "off" option). */
  leftLabel?: ReactNode;
  /** Optional label rendered to the right of the switch (typically the "on" option). */
  rightLabel?: ReactNode;
  /** Icon rendered inside the moving circle. Defaults to `'Checkmark'`. */
  icon?: SvgIconNames;
  /**
   * When the `icon` is shown:
   * `'only-on'` (default) shows it when checked,
   * `'only-off'` shows it when unchecked,
   * `'both'` always shows it,
   * `'none'` hides it.
   */
  iconVisibility?: IconVisibility;
  /**
   * Which side label gets the bold/solid treatment:
   * `'both'` (default) keeps both bold,
   * `'only-on'` bolds only the side that matches the current state.
   */
  labelHighlight?: HighlightMode;
  /**
   * Whether the track gets the accent background:
   * `'only-on'` (default) only when checked,
   * `'both'` always.
   */
  backgroundHighlight?: HighlightMode;
  /** Size preset. Defaults to `'small'`. */
  size?: 'medium' | 'small';
  /** Disables clicks and applies the muted visual treatment. */
  disabled?: boolean;
}

/**
 * Boolean on/off switch with optional left/right labels and a small
 * icon inside the moving circle.
 *
 * Tracks `isChecked` into local state on every change, so it can be
 * driven externally (as the source of truth) or managed locally
 * (uncontrolled — initial value only). Use `onToggle` to react to the
 * user toggling.
 */
export const Toggle = ({
  id,
  isChecked = false,
  onToggle,
  leftLabel,
  rightLabel,
  icon = 'Checkmark',
  iconVisibility = 'only-on',
  labelHighlight = 'both',
  backgroundHighlight = 'only-on',
  size = 'small',
  className,
  disabled = false,
  ...rest
}: ToggleProps) => {
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;

    if (onToggle) {
      onToggle(newValue, e);
    }
  };

  const shouldShowIcon =
    iconVisibility == 'both' ||
    (!checked && iconVisibility === 'only-off') ||
    (checked && iconVisibility === 'only-on');

  const isLeftSolid =
    (!checked && labelHighlight === 'only-on') || labelHighlight === 'both';
  const isRightSolid =
    (checked && labelHighlight === 'only-on') || labelHighlight === 'both';
  const isBackgroundActive =
    (checked && backgroundHighlight === 'only-on') ||
    backgroundHighlight === 'both';

  return (
    <label
      htmlFor={id}
      className={clsx(styles.root, className, {
        [styles.disabled]: disabled,
      })}
    >
      {leftLabel && (
        <span
          className={clsx(styles.labelText, {
            [styles.labelSolid]: isLeftSolid,
          })}
          data-testid="toggle-left-text"
        >
          {leftLabel}
        </span>
      )}
      <input
        className={styles.toggleInput}
        id={id}
        type="checkbox"
        checked={checked}
        aria-checked={isChecked}
        onChange={handleOnChange}
        data-testid="toggle-ds"
        {...rest}
      />
      <span
        className={clsx(styles.container, styles[size], {
          [styles.backgroundActive]: isBackgroundActive,
          [styles.checked]: checked,
        })}
        data-testid="toggle-container"
        data-toggle-container
        role="switch"
        aria-checked={checked}
      >
        <span
          className={clsx(
            styles.circle,
            size === 'medium' ? styles.circleMedium : styles.circleSmall,
            { [styles.circleChecked]: checked }
          )}
          data-toggle-circle
        >
          {icon && (
            <Icon
              className={clsx(styles.icon, {
                [styles.iconVisible]: shouldShowIcon,
              })}
              iconName={icon}
              size={size === 'medium' ? 14 : 10}
              data-toggle-icon
            />
          )}
        </span>
      </span>
      {rightLabel && (
        <span
          className={clsx(styles.labelText, {
            [styles.labelSolid]: isRightSolid,
          })}
        >
          {rightLabel}
        </span>
      )}
    </label>
  );
};
